// src/pages/ttc/TTCPages.jsx
// Exports: TTCHealthRecord, TTCCommunity, TTCConnect, TTCEvents, TTCProducts
// Routes:
//   /demo/ttc/health     → TTCHealthRecord
//   /demo/ttc/community  → TTCCommunity
//   /demo/ttc/connect    → TTCConnect
//   /demo/ttc/events     → TTCEvents
//   /demo/ttc/products   → TTCProducts

import { useState } from "react";
import { NovaUnifiedPageBar } from "../../components/NovaUnified";
import SectionHeader from "../../components/SectionHeader";
import { demoChapterStackColumnStyle, demoSectionCardStackStyle, DEMO_MINI_CARD_GRID_GAP_PX, DEMO_SECTION_CARD_STACK_GAP_PX } from "../../designSystem";

function DemoSectionHeader({ title, descriptor, trailing }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
      <div style={{ flex: "1 1 220px", minWidth: 0 }}>
        <SectionHeader title={title} descriptor={descriptor} />
      </div>
      {trailing}
    </div>
  );
}

const C = {
  primary: "var(--terracotta)", primaryL: "var(--terracotta-l)", primaryM: "var(--terracotta-m)", primaryDk: "var(--terracotta-d)",
  sand: "var(--sand)", sand2: "var(--sand-2)", sand3: "var(--sand-3)", white: "var(--white)", card: "var(--card)", border: "var(--border)",
  muted: "var(--muted)", text: "var(--text)", text2: "var(--text-2)",
  sage: "var(--sage)", sageL: "var(--sage-l)", sageM: "var(--sage-m)", sageD: "var(--sage-d)",
  ppL: "var(--purple-l)", ppM: "var(--purple-m)", ppD: "var(--purple-d)", r: "var(--r)",
};

const Card = ({ children, style = {} }) => (
  <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: C.r, padding: "16px 18px", ...style }}>{children}</div>
);
const Btn = ({ children, variant = "primary", style = {}, onClick }) => {
  const vs = {
    primary: { background: C.primary, color: "#fff", border: "none" },
    ghost: { background: C.primaryL, color: C.primaryDk, border: `1px solid ${C.primaryM}` },
    sand: { background: C.sand, color: C.muted, border: `1px solid ${C.border}` },
  };
  return <button onClick={onClick} style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 700, borderRadius: 999, padding: "7px 16px", cursor: "pointer", ...vs[variant], ...style }}>{children}</button>;
};
const Tag = ({ children, bg = C.primaryL, color = C.primaryDk, border = C.primaryM }) => (
  <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 9px", borderRadius: 999, background: bg, color, border: `1px solid ${border}`, display: "inline-block" }}>{children}</span>
);
const NovaBanner = ({ text, prompts = [], onOpenNova, contextPill = "Guidance" }) => (
  <NovaUnifiedPageBar
    layout="stacked"
    contextPill={contextPill}
    body={
      <>
        <div style={{ fontSize: 13, color: "#4b1528", lineHeight: 1.55, marginBottom: prompts.length ? 8 : 0 }}>{text}</div>
        {prompts.length > 0 && (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 4 }}>
            {prompts.map(p => <button key={p} type="button" style={{ fontFamily: "inherit", fontSize: 11, padding: "4px 10px", background: C.white, border: `1px solid ${C.primaryM}`, borderRadius: 999, cursor: "pointer", color: C.primaryDk }}>{p}</button>)}
          </div>
        )}
      </>
    }
    actionLabel="Open Nova →"
    onAction={onOpenNova || (() => {})}
  />
);

// ══════════════════════════════════════════════════════════════════════
// TTC HEALTH RECORD
// ══════════════════════════════════════════════════════════════════════
export function TTCHealthRecord({ navigate }) {
  const nav = navigate || (() => {});
  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans',-apple-system,sans-serif", ...demoChapterStackColumnStyle }}>
        <NovaBanner
          text="Your cycle looks regular at 28 days. Based on your OPK pattern this month, your fertile window aligns well with cycle day 14. No red flags — Nova will let you know if patterns shift."
          prompts={["What do my hormone levels mean?", "Is my cycle length normal?", "When should I get tested?"]}
          onOpenNova={() => nav("/nova-chat")}
        />

        <div style={demoSectionCardStackStyle}>
        {/* Hormone panel */}
        <div>
        <DemoSectionHeader title="Hormone panel" descriptor="Labs you’ve had and what your OPKs suggest this cycle — when you’re ready to add them." />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: DEMO_MINI_CARD_GRID_GAP_PX }}>
          {[
            { label: "AMH", value: "—", sub: "Not yet tested", note: "Ask your doctor" },
            { label: "FSH (CD3)", value: "—", sub: "Not yet tested", note: "Day 3 test" },
            { label: "LH (OPK)", value: "Rising", sub: "CD 13: almost +", note: "Peak expected today", color: C.primary },
            { label: "TSH", value: "—", sub: "Not yet tested", note: "Thyroid check" },
          ].map(s => (
            <div key={s.label} style={{ background: C.sand, borderRadius: C.r, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{s.label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: s.color || C.text }}>{s.value}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{s.sub}</div>
              <div style={{ fontSize: 10, color: C.primary, marginTop: 3 }}>{s.note}</div>
            </div>
          ))}
        </div>
        </div>

        {/* OPK pattern + BBT */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: DEMO_SECTION_CARD_STACK_GAP_PX }}>
          <Card>
            <DemoSectionHeader title="OPK pattern — this cycle" descriptor="Strip readings day by day so Nova can see your surge timing." />
            {[
              { day: "CD 10", result: "Negative", bar: 0.2 },
              { day: "CD 11", result: "Negative", bar: 0.35 },
              { day: "CD 12", result: "Low positive", bar: 0.55 },
              { day: "CD 13", result: "Almost positive", bar: 0.75 },
              { day: "CD 14", result: "Log now →", bar: null, today: true },
            ].map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.sand2}` }}>
                <div style={{ width: 36, fontSize: 11, fontWeight: 700, color: r.today ? C.primary : C.muted, flexShrink: 0 }}>{r.day}</div>
                <div style={{ flex: 1, height: 6, borderRadius: 999, background: C.sand2 }}>
                  {r.bar && <div style={{ width: `${r.bar * 100}%`, height: "100%", background: r.bar >= 0.7 ? C.primary : C.primaryM, borderRadius: 999 }} />}
                </div>
                <div style={{ fontSize: 11, color: r.today ? C.primary : C.muted, width: 90, textAlign: "right" }}>{r.result}</div>
              </div>
            ))}
          </Card>
          <Card>
            <DemoSectionHeader title="BBT chart" descriptor="Log daily temps to confirm ovulation after your LH peak." />
            <div style={{ background: C.sand, borderRadius: 10, height: 140, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, marginBottom: 6 }}>📈</div>
                <div style={{ fontSize: 12, color: C.muted }}>Log BBT daily to see your chart</div>
                <Btn variant="ghost" style={{ marginTop: 8, fontSize: 11 }}>Start logging</Btn>
              </div>
            </div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>A temp rise of 0.2°F+ after ovulation confirms you ovulated. Log every morning before getting up.</div>
          </Card>
        </div>

        {/* When to test + doctor guidance */}
        <Card>
          <DemoSectionHeader
            title="Fertility workup — when to ask"
            descriptor="Typical timelines for trying naturally and when a specialist visit makes sense."
            trailing={<Btn variant="ghost" style={{ fontSize: 11, textTransform: "none", letterSpacing: 0 }}>Find a specialist →</Btn>}
          />
          {[
            { label: "Trying since", value: "Nov 2025 · 4 months", status: "Normal — continue naturally" },
            { label: "Recommended workup at", value: "Nov 2026 (12 months)", status: "Or sooner if cycles change" },
            { label: "Over 35?", value: "Seek workup at 6 months", status: "Sooner if you're 35+" },
          ].map(r => (
            <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.sand2}`, fontSize: 12 }}>
              <div>
                <div style={{ fontWeight: 600, color: C.text }}>{r.label}</div>
                <div style={{ color: C.muted, marginTop: 1 }}>{r.value}</div>
              </div>
              <Tag bg={C.sageL} color={C.sageD} border={C.sageM}>{r.status}</Tag>
            </div>
          ))}
        </Card>
        </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// TTC COMMUNITY
// ══════════════════════════════════════════════════════════════════════
export function TTCCommunity({ navigate }) {
  const nav = navigate || (() => {});
  const [tab, setTab] = useState("feed");
  const POSTS = [
    { init: "S", bg: C.primaryM, color: C.primaryDk, name: "Sarah K.", badge: "cycle day 14", date: "3/23/2026", body: "PEAK OPK today!! Anyone else testing positive on cycle day 14? This is my 4th month and first time getting such a strong positive 🙏", hearts: 34, boats: 21, replies: 8 },
    { init: "J", bg: C.ppM, color: C.ppD, name: "Jamie R.", badge: null, date: "3/23/2026", body: "Starting the TWW tonight after confirmed ovulation. Please send all the positive vibes. Month 6 over here and trying to stay sane 🤍", hearts: 67, boats: 44, replies: 12 },
    { init: "M", bg: C.sageM, color: C.sageD, name: "Maya", badge: "friend", date: "3/23/2026", body: "Just got my OPK out — wish me luck! CD 14 and I can feel the flutters of hope. This TTC community keeps me going on the hard days.", hearts: 89, boats: 31, replies: 6 },
  ];
  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans',-apple-system,sans-serif" }}>
      <div className="page-header-block chapter-ttc" style={{ marginBottom: 12 }}>
        <div className="page-eyebrow">💜 TTC · Community</div>
        <div className="page-title-card" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <h1 className="page-title">Your TTC community</h1>
            <p className="page-descriptor">Matched to your cycle day and situation.</p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
            <span className="page-badge-ttc">Cycle 4 · Day 14</span>
            <button type="button" className="page-cta-ttc">Join the waitlist</button>
          </div>
        </div>
        <div className="page-pills-ttc">
          <span className="pill-ttc"><span className="pill-dot-ttc" />Fertile window · 12 online</span>
          <span className="pill-ttc">OPK reading room live</span>
          <span className="pill-ttc">Matched · Cycle 4 · Columbus</span>
        </div>
      </div>
      <div style={{ display: "flex", borderBottom: `1px solid ${C.border}` }}>
        {["feed","rooms","groups","friends"].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: tab === t ? C.primaryDk : C.muted, background: "none", border: "none", borderBottom: `2px solid ${tab === t ? C.primary : "transparent"}`, padding: "10px 16px", cursor: "pointer", textTransform: "capitalize" }}>{t === "rooms" ? "Live rooms" : t === "feed" ? "My Chapter" : t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>
      <div style={{ padding: 14 }}>
        <NovaBanner
          contextPill="Community"
          text="94 women in TTC are online right now. The Fertile Window room is active — 12 women timing intercourse this week and comparing OPK results."
          prompts={["Who's in the TWW right now?", "Which group is for month 4+?", "Find women near me TTC"]}
          onOpenNova={() => nav("/nova-chat")}
        />
        <div style={{ background: C.primaryL, border: `1px solid ${C.primaryM}`, borderRadius: C.r, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, color: C.primaryDk }}>💜 TTC chapter — everyone here can see your posts.</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.primary }}>8,200 members</div>
        </div>
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: C.r, padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.primaryM, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: C.primaryDk }}>M</div>
            <input placeholder="Share something with your chapter..." style={{ flex: 1, fontFamily: "inherit", fontSize: 13, border: `1px solid ${C.border}`, borderRadius: 999, padding: "8px 14px", background: C.sand, color: C.muted }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
            <div style={{ display: "flex", gap: 6 }}>
              <Btn variant="sand" style={{ fontSize: 11, padding: "4px 12px" }}>📷 Photo</Btn>
              <Btn variant="sand" style={{ fontSize: 11, padding: "4px 12px" }}>🎥 Video</Btn>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Tag>💜 TTC</Tag>
              <Btn variant="primary" style={{ fontSize: 12, padding: "6px 16px" }}>Post</Btn>
            </div>
          </div>
        </div>
        <DemoSectionHeader
          title="What's happening in your chapter"
          descriptor="Posts from people on a similar timeline — sort by recent or top."
          trailing={
            <div style={{ display: "flex", gap: 5 }}>
              <Btn variant="sand" style={{ fontSize: 11, padding: "4px 10px" }}>Recent</Btn>
              <Btn variant="sand" style={{ fontSize: 11, padding: "4px 10px" }}>Top</Btn>
            </div>
          }
        />
        {POSTS.map((post, i) => (
          <div key={i} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: C.r, padding: "13px 15px", marginBottom: 10 }}>
            <div style={{ display: "flex", gap: 9, marginBottom: 8 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: post.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: post.color, flexShrink: 0 }}>{post.init}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  {post.name}
                  {post.badge && <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 999, background: C.primaryL, color: C.primaryDk, border: `1px solid ${C.primaryM}`, marginLeft: 6 }}>{post.badge}</span>}
                </div>
                <div style={{ fontSize: 11, color: C.muted }}>{post.date}</div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.6, marginBottom: 10 }}>{post.body}</div>
            <div style={{ display: "flex", borderTop: `1px solid ${C.sand2}`, paddingTop: 7 }}>
              <button style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: C.primary, background: "none", border: "none", flex: 1, cursor: "pointer" }}>❤️ {post.hearts}</button>
              <button style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: C.sageD, background: "none", border: "none", flex: 1, cursor: "pointer" }}>🤝 Same boat · {post.boats}</button>
              <button style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: C.muted, background: "none", border: "none", flex: 1, cursor: "pointer" }}>💬 Reply · {post.replies}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// TTC CONNECT (Women Like Me + Live Rooms + Groups)
// ══════════════════════════════════════════════════════════════════════
export function TTCConnect({ navigate }) {
  const nav = navigate || (() => {});
  const [tab, setTab] = useState("women");
  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans',-apple-system,sans-serif" }}>
      <div className="page-header-block chapter-ttc" style={{ marginBottom: 12 }}>
        <div className="page-eyebrow">💜 TTC · Connect</div>
        <div className="page-title-card" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <h1 className="page-title">Women like you</h1>
            <p className="page-descriptor">Matched by Nova to your cycle day, timeline, and situation.</p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
            <span className="page-badge-ttc">Cycle 4 · Day 14</span>
            <button type="button" className="page-cta-ttc">Join the waitlist</button>
          </div>
        </div>
        <div className="page-pills-ttc">
          <span className="pill-ttc"><span className="pill-dot-ttc" />Cycle 4 · Day 14</span>
          <span className="pill-ttc">Riley matched this week</span>
          <span className="pill-ttc">Same OPK timing as you</span>
        </div>
      </div>
      <div style={{ display: "flex", borderBottom: `1px solid ${C.border}` }}>
        {[["women","💕 Women like me"],["rooms","🟢 Live rooms"],["groups","👥 Groups & forums"]].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: tab === id ? C.primaryDk : C.muted, background: "none", border: "none", borderBottom: `2px solid ${tab === id ? C.primary : "transparent"}`, padding: "11px 16px", cursor: "pointer", whiteSpace: "nowrap" }}>{label}</button>
        ))}
      </div>
      <div style={{ padding: 14 }}>
        {tab === "women" && (
          <div>
            <div style={{ background: C.primaryL, border: `1px solid ${C.primaryM}`, borderRadius: C.r, padding: "14px 16px", marginBottom: 14 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.primaryDk, marginBottom: 3 }}>Women going through this with you</div>
              <div style={{ fontSize: 12, color: C.primaryDk }}>Nova matched you on cycle day 14 · TTC month 4 · Columbus, OH</div>
            </div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 14 }}>
              {["All matches","Same cycle day","Same month trying","Same city","Similar pattern"].map(f => (
                <button key={f} style={{ fontFamily: "inherit", fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 999, border: `1px solid ${f === "All matches" ? C.primaryM : C.border}`, background: f === "All matches" ? C.primaryL : C.sand, color: f === "All matches" ? C.primaryDk : C.muted, cursor: "pointer" }}>{f}</button>
              ))}
            </div>
            <DemoSectionHeader title="Your closest match" descriptor="Nova’s best match for your cycle day, month trying, and location." />
            <div style={{ background: C.white, border: `2px solid ${C.primary}`, borderRadius: C.r, padding: 16, marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.primaryM, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: C.primaryDk }}>JK</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Jessica K.</div>
                  <div style={{ fontSize: 11, color: C.muted }}>TTC · Month 4 · Cycle day 14 · Columbus, OH</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: C.primaryL, color: C.primaryDk, border: `1px solid ${C.primaryM}` }}>95% match</span>
              </div>
              <div style={{ display: "flex", gap: 5, marginBottom: 10, flexWrap: "wrap" }}>
                <Tag>Same cycle day</Tag>
                <Tag bg={C.sageL} color={C.sageD} border={C.sageM}>Same city</Tag>
                <Tag bg={C.ppL} color={C.ppD} border={C.ppM}>Month 4</Tag>
              </div>
              <div style={{ fontStyle: "italic", fontSize: 13, color: C.text2, background: C.sand, borderRadius: 8, padding: "8px 12px", borderLeft: `3px solid ${C.primaryM}`, marginBottom: 12 }}>"Just got my positive OPK today! Cycle day 14, month 4. This community is the only place I can be honest about how hard the waiting is."</div>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="primary" style={{ flex: 1 }}>Connect</Btn>
                <Btn variant="ghost" style={{ flex: 1 }}>Message</Btn>
              </div>
            </div>
            {[
              { init: "AR", bg: C.sageM, color: C.sageD, name: "Aisha R.", sub: "TTC month 3 · CD 14 · Cleveland", pct: "91%", tags: ["Same cycle day","Month 3"] },
              { init: "NP", bg: C.ppM, color: C.ppD, name: "Nadia P.", sub: "TTC month 5 · irregular cycles · Columbus", pct: "87%", tags: ["Same city","Irregular cycles"] },
              { init: "LT", bg: "#b5d4f4", color: "#185fa5", name: "Lauren T.", sub: "TTC month 4 · PCOS · Cincinnati", pct: "84%", tags: ["Month 4","PCOS"] },
            ].map((m, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", background: C.card, border: `1px solid ${C.border}`, borderRadius: C.r, marginBottom: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: m.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: m.color }}>{m.init}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{m.name} <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 999, background: C.primaryL, color: C.primaryDk, border: `1px solid ${C.primaryM}` }}>{m.pct}</span></div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{m.sub}</div>
                  <div style={{ display: "flex", gap: 4, marginTop: 5 }}>{m.tags.map(t => <Tag key={t} style={{ fontSize: 10 }}>{t}</Tag>)}</div>
                </div>
                <Btn variant="primary" style={{ fontSize: 11, padding: "5px 14px" }}>Connect</Btn>
              </div>
            ))}
          </div>
        )}

        {tab === "rooms" && (
          <div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>Real-time support from women going through the same thing <strong style={{ color: C.text }}>right now.</strong></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: DEMO_MINI_CARD_GRID_GAP_PX }}>
              {[
                { name: "Fertile window timing", desc: "OPK results, BD timing, fertile signs", count: 12, hot: true },
                { name: "Two-week wait", desc: "Symptom spotting, managing the wait, testing", count: 18 },
                { name: "OPK help & reading", desc: "Is this positive? Reading strips together", count: 7 },
                { name: "Month 4–6 support", desc: "When natural TTC starts to feel harder", count: 9 },
                { name: "PCOS & irregular cycles", desc: "Tracking with unpredictable cycles", count: 5 },
              ].map(room => (
                <div key={room.name} style={{ background: room.hot ? C.primaryL : C.white, border: `1px solid ${room.hot ? C.primaryM : C.border}`, borderRadius: C.r, padding: "13px 14px" }}>
                  {room.hot && <span style={{ fontSize: 10, fontWeight: 700, background: C.primary, color: "#fff", padding: "2px 7px", borderRadius: 999, display: "inline-block", marginBottom: 6 }}>Active now</span>}
                  <div style={{ fontSize: 13, fontWeight: 700, color: room.hot ? C.primaryDk : C.text, marginBottom: 4 }}>{room.name}</div>
                  <div style={{ fontSize: 12, color: room.hot ? C.primaryDk : C.muted, marginBottom: 10, lineHeight: 1.4 }}>{room.desc}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: C.sageD }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} />{room.count} online</div>
                    <Btn variant={room.hot ? "primary" : "sand"} style={{ fontSize: 11, padding: "4px 12px" }}>Join</Btn>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "groups" && (
          <div>
            <DemoSectionHeader title="Your groups" descriptor="Circles you’ve joined — OPKs, timing, and chapter-wide support." />
            {[{ icon: "💜", name: "TTC — First Year", meta: "4,200 members · your month", joined: true },{ icon: "📅", name: "Ovulation & Timing", meta: "3,100 members · OPKs & fertile windows", joined: true }].map(g => (
              <div key={g.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", background: C.card, border: `1px solid ${C.border}`, borderRadius: C.r, marginBottom: 8 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: C.primaryL, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{g.icon}</div>
                <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{g.name}</div><div style={{ fontSize: 11, color: C.muted }}>{g.meta}</div></div>
                {g.joined ? <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: C.sageL, color: C.sageD, border: `1px solid ${C.sageM}` }}>Joined</span> : <Btn variant="ghost" style={{ fontSize: 11, padding: "4px 12px" }}>Join →</Btn>}
              </div>
            ))}
            <div style={{ marginTop: 6 }}>
              <DemoSectionHeader title="Suggested for you" descriptor="Based on your chapter, timing, and what Nova sees in your pattern." />
            </div>
            {[{ icon: "🔁", name: "TWW Support Circle", meta: "2,800 members · Nova matched you" },{ icon: "🌿", name: "Natural TTC", meta: "1,900 members · no interventions yet" },{ icon: "💜", name: "PCOS Sisters TTC", meta: "4,100 members" }].map(g => (
              <div key={g.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", background: C.card, border: `1px solid ${C.border}`, borderRadius: C.r, marginBottom: 8 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: C.primaryL, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{g.icon}</div>
                <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{g.name}</div><div style={{ fontSize: 11, color: C.muted }}>{g.meta}</div></div>
                <Btn variant="ghost" style={{ fontSize: 11, padding: "4px 12px" }}>Join →</Btn>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// TTC EVENTS
// ══════════════════════════════════════════════════════════════════════
export function TTCEvents({ navigate }) {
  const nav = navigate || (() => {});
  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans',-apple-system,sans-serif", ...demoChapterStackColumnStyle }}>
        <NovaBanner
          contextPill="Events"
          text="Nova found 5 TTC-relevant events this month near Columbus, OH — including a fertility nutrition workshop on March 28 and a virtual OPK reading class tomorrow."
          prompts={["Find virtual TTC events","Fertility specialists near me","TTC support groups Ohio"]}
          onOpenNova={() => nav("/nova-chat")}
        />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          {["All","In person","Virtual","This week","This month"].map(f => (
            <button key={f} style={{ fontFamily: "inherit", fontSize: 11, fontWeight: 600, padding: "5px 13px", borderRadius: 999, border: `1px solid ${f === "All" ? C.primaryM : C.border}`, background: f === "All" ? C.primaryL : C.sand, color: f === "All" ? C.primaryDk : C.muted, cursor: "pointer" }}>{f}</button>
          ))}
        </div>
        <DemoSectionHeader title="This week" descriptor="Workshops, support circles, and classes Nova flagged for your area." />
        {[
          { date: "MAR\n24", title: "OPK Reading & Fertile Window Q&A", org: "OrivonAI · virtual", desc: "Live Q&A with a fertility nurse educator. Bring your OPK strips, get your results read in real time.", tags: ["Virtual","Free","Nova matched"], hot: true },
          { date: "MAR\n26", title: "TTC Support Circle — Columbus", org: "RESOLVE · Ohio", desc: "Monthly meetup for women trying to conceive. Share your story, ask questions, find your people.", tags: ["In person","Columbus OH","Free"] },
          { date: "MAR\n28", title: "Fertility Nutrition Workshop", org: "Ohio Functional Wellness · Columbus", desc: "What to eat (and avoid) when TTC. CoQ10, folate, seed cycling, blood sugar — evidence-based approach.", tags: ["In person","$25","Nutrition"] },
        ].map((ev, i) => (
          <div key={i} style={{ background: ev.hot ? C.primaryL : C.white, border: `1px solid ${ev.hot ? C.primaryM : C.border}`, borderRadius: C.r, padding: 16, marginBottom: 10, display: "flex", gap: 14 }}>
            <div style={{ flexShrink: 0, textAlign: "center", background: ev.hot ? C.primary : C.sand, color: ev.hot ? "#fff" : C.text, borderRadius: 10, padding: "8px 12px", minWidth: 52 }}>
              {ev.date.split("\n").map((l, j) => <div key={j} style={{ fontSize: j === 0 ? 10 : 16, fontWeight: 700, lineHeight: 1.1 }}>{l}</div>)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 5 }}>
                {ev.tags.map(t => <Tag key={t} bg={t === "Nova matched" ? C.ppL : C.sand2} color={t === "Nova matched" ? C.ppD : C.muted} border={t === "Nova matched" ? C.ppM : C.border}>{t}</Tag>)}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: ev.hot ? C.primaryDk : C.text, marginBottom: 3 }}>{ev.title}</div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{ev.org}</div>
              <div style={{ fontSize: 12, color: ev.hot ? C.primaryDk : C.text2, lineHeight: 1.5 }}>{ev.desc}</div>
            </div>
            <Btn variant={ev.hot ? "primary" : "ghost"} style={{ alignSelf: "flex-end", whiteSpace: "nowrap" }}>{ev.hot ? "Join →" : "Join"}</Btn>
          </div>
        ))}
        <div style={{ marginTop: 6 }}>
          <DemoSectionHeader title="Coming up" descriptor="Save the date — virtual and in-person options for the next few weeks." />
        </div>
        {[
          { date: "APR\n5", title: "When to See a Fertility Specialist", org: "Ohio Fertility Network · virtual", desc: "Free webinar: how long to try naturally, what a workup involves, and questions to ask your first appointment.", tags: ["Virtual","Free"] },
          { date: "APR\n12", title: "Mind-Body TTC Workshop", org: "Columbus Integrative Health", desc: "Acupuncture, stress reduction, and cycle support for women TTC. Held monthly.", tags: ["In person","$40","Columbus OH"] },
        ].map((ev, i) => (
          <div key={i} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: C.r, padding: 16, marginBottom: 8, display: "flex", gap: 14 }}>
            <div style={{ flexShrink: 0, textAlign: "center", background: C.sand, color: C.text, borderRadius: 10, padding: "8px 12px", minWidth: 52 }}>
              {ev.date.split("\n").map((l, j) => <div key={j} style={{ fontSize: j === 0 ? 10 : 16, fontWeight: 700, lineHeight: 1.1 }}>{l}</div>)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 5 }}>{ev.tags.map(t => <Tag key={t} bg={C.sand2} color={C.muted} border={C.border}>{t}</Tag>)}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 3 }}>{ev.title}</div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{ev.org}</div>
              <div style={{ fontSize: 12, color: C.text2, lineHeight: 1.5 }}>{ev.desc}</div>
            </div>
            <Btn variant="ghost" style={{ alignSelf: "flex-end" }}>Join</Btn>
          </div>
        ))}
        <div style={{ background: C.primary, borderRadius: C.r, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>Submit a TTC event near you</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", marginTop: 2 }}>Support groups, workshops, specialist talks — share with your chapter</div>
          </div>
          <button onClick={() => nav("/signup")} style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 700, color: "var(--terracotta-d)", background: "var(--white)", border: "none", borderRadius: 999, padding: "8px 18px", cursor: "pointer", whiteSpace: "nowrap" }}>+ Submit event</button>
        </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// TTC PRODUCTS
// ══════════════════════════════════════════════════════════════════════
export function TTCProducts({ navigate }) {
  const nav = navigate || (() => {});
  const [filter, setFilter] = useState("all");
  const PRODUCTS = [
    { brand: "Clearblue", name: "Clearblue Advanced Digital OPK", desc: "Detects the full fertile window — LH and estrogen. Smiley face = no guessing.", price: "$40", tag: "OPK", tagType: "opk", digs: 1247, rating: 4.9, reviews: "12.4k", featured: true },
    { brand: "Mira", name: "Mira Fertility Tracker", desc: "Quantitative LH readings — shows your actual hormone numbers, not just positive/negative.", price: "$199", tag: "OPK", tagType: "opk", digs: 892, rating: 4.7, reviews: "3.2k" },
    { brand: "Ritual", name: "Essential Prenatal Multivitamin", desc: "OB-recommended. Folate (not folic acid), DHA, D3. Start 3 months before conceiving.", price: "$35", tag: "Supplements", tagType: "supplements", digs: 1023, rating: 4.8, reviews: "8.1k" },
    { brand: "Natalist", name: "Prenatal Daily Packets", desc: "Everything in one packet. Folate, CoQ10, omega-3, iron, D3. Built for TTC.", price: "$45", tag: "Supplements", tagType: "supplements", digs: 756, rating: 4.6, reviews: "2.8k" },
    { brand: "Easy@Home", name: "BBT Basal Thermometer", desc: "0.01° precision, memory recall, no beep mode. The TTC standard.", price: "$12", tag: "Tracking", tagType: "tracking", digs: 634, rating: 4.8, reviews: "31k" },
    { brand: "Proov", name: "Proov PdG Test Strips", desc: "Confirms ovulation actually happened by testing progesterone metabolite. Unique in the market.", price: "$30", tag: "OPK", tagType: "opk", digs: 521, rating: 4.5, reviews: "4.2k" },
  ];
  const filtered = filter === "all" ? PRODUCTS : PRODUCTS.filter(p => p.tagType === filter);
  const tagColors = { opk: { bg: C.primaryL, color: C.primaryDk, border: C.primaryM }, supplements: { bg: C.sageL, color: C.sageD, border: C.sageM }, tracking: { bg: C.ppL, color: C.ppD, border: C.ppM } };

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans',-apple-system,sans-serif", ...demoChapterStackColumnStyle }}>
        <NovaBanner
          contextPill="Products"
          text="You're on cycle day 14 — peak fertile window. Nova has surfaced the most-used OPKs and tracking tools for women in your exact stage."
          prompts={["Best OPK for PCOS","Do I need CoQ10?","Which prenatal has folate not folic acid?"]}
          onOpenNova={() => nav("/nova-chat")}
        />
        <DemoSectionHeader
          title="Filter"
          descriptor="Narrow the catalog by OPKs, supplements, and tracking tools."
          trailing={
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              {[["all","All"],["opk","OPK & testing"],["supplements","Supplements"],["tracking","Tracking"]].map(([id,label]) => (
                <button key={id} onClick={() => setFilter(id)} style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 600, padding: "5px 14px", borderRadius: 999, border: `1px solid ${filter === id ? C.primaryM : C.border}`, background: filter === id ? C.primaryL : C.sand, color: filter === id ? C.primaryDk : C.muted, cursor: "pointer" }}>{label}</button>
              ))}
            </div>
          }
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: DEMO_MINI_CARD_GRID_GAP_PX }}>
          {filtered.map(p => {
            const tc = tagColors[p.tagType];
            return (
              <div key={p.name} style={{ background: C.white, border: `${p.featured ? "2px" : "1px"} solid ${p.featured ? C.primary : C.border}`, borderRadius: C.r, overflow: "hidden", display: "flex", flexDirection: "column", position: "relative" }}>
                {p.featured && <div style={{ position: "absolute", top: -1, left: 14, background: C.primary, color: "#fff", fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: "0 0 8px 8px" }}>Nova's top pick</div>}
                <div style={{ height: 120, background: C.sand, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, position: "relative" }}>
                  🧪
                  <div style={{ position: "absolute", top: 10, left: 10, background: "rgba(255,253,249,.92)", borderRadius: 999, padding: "3px 8px", fontSize: 10, fontWeight: 700, color: C.text }}>★ {p.rating} <span style={{ fontSize: 10, color: C.muted }}>({p.reviews})</span></div>
                  <div style={{ position: "absolute", bottom: 8, right: 8, background: C.primary, color: "#fff", fontSize: 11, fontWeight: 800, padding: "5px 11px", borderRadius: 999 }}>{p.digs} digs</div>
                </div>
                <div style={{ padding: "12px 14px", flex: 1, display: "flex", flexDirection: "column" }}>
                  <div className="card-label" style={{ marginBottom: 3 }}>
                    <span>{p.brand}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.45, flex: 1, marginBottom: 10 }}>{p.desc}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{p.price}</div>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 999, background: tc.bg, color: tc.color, border: `1px solid ${tc.border}` }}>{p.tag}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ background: C.primary, borderRadius: C.r, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>Sign up to access the full TTC catalog</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", marginTop: 2 }}>150+ TTC products — OPKs, supplements, tracking tools, and more</div>
          </div>
          <button onClick={() => nav("/signup")} style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 700, color: "var(--terracotta-d)", background: "var(--white)", border: "none", borderRadius: 999, padding: "8px 18px", cursor: "pointer", whiteSpace: "nowrap" }}>Sign up free →</button>
        </div>
    </div>
  );
}
